import { autobind } from 'core-decorators';
import PropTypes from 'prop-types';
import { Component } from 'react';
import CornDensityChart from '/@/components/corn-density-chart';
import CreatedBy from '/@/components/created-by';
import Metrics from '/@/components/metrics';
import Notes from '/@/components/notes';
import { BrandValues, Closing, Cover, DensityTable, LegalText, Refuge } from '/@/components/product-datasheet';
import Title from '/@/components/title';
import { UnitConverter } from '/@/core/units';
import { _ } from '/@/locale';

import '/@/styles/index.css';
import TechnologyList from '/@/views/catalog/technology-list';
import styles from './styles.module.css';

@autobind
export default class ProductDatasheetPDF extends Component {
    static childContextTypes = {
        print: PropTypes.bool,
        units: PropTypes.object,
        store: PropTypes.object,
    };

    getChildContext() {
        return {
            print: true,
            units: new UnitConverter({
                ...this.props.units,
                language: this.props.language,
                brands: this.props.appInstance.brands.reduce(
                    (brandsByType, brand) => ({
                        ...brandsByType,
                        [brand.type]: brand,
                    }),
                    {}
                ),
            }),
            store: {
                getState: () => ({
                    session: {
                        user: {},
                    },
                }),
                dispatch: () => {},
                subscribe: () => {},
            },
        };
    }

    shouldRenderOpening() {
        return this.props.productDatasheet.extrasIds.includes('openingImage');
    }

    shouldRenderClosing() {
        return this.props.productDatasheet.extrasIds.includes('closingImage');
    }

    hasDensityGraphic = (profile) => (profile.environmentRanges || []).length > 0;

    hasDensityTable = (profile) => !!profile.densityTableConstraints.length;

    hasDensity(profile, type) {
        if (this.props.productDatasheet.type !== type) return false;
        if (!this.props.productDatasheet.modulesIds.includes('densities')) return false;
        if (!this.hasDensityGraphic(profile) && !this.hasDensityTable(profile)) return false;
        return true;
    }

    shouldRenderMetrics(profile) {
        return this.props.productDatasheet.modulesIds.includes('metrics');
    }

    shouldRenderFeatures(profile) {
        return this.props.productDatasheet.modulesIds.includes('features');
    }

    shouldRenderRefuge(profile) {
        return this.props.productDatasheet.modulesIds.includes('refugeOptions');
    }

    shouldRenderBrandValues() {
        return this.props.productDatasheet.extrasIds.includes('brandValues');
    }

    shouldRenderLegalText() {
        return this.props.productDatasheet.extrasIds.includes('legalText');
    }

    renderProductDatasheet(profile) {
        const { shortDescription, notes, technologies } = profile;
        const { brand, type } = this.props.productDatasheet;
        let environmentRange;
        if (this.hasDensity(profile, 'corn') && this.hasDensityGraphic(profile)) {
            const environmentRangeId = this.props.productDatasheet.selectedEnvironmentRanges[profile.id];
            environmentRange = profile.environmentRanges.find((el) => el.referenceValue === environmentRangeId);
        }

        return (
            <div className={styles.root} key={profile.id}>
                {/* NAME */}
                <div>
                    <div className={styles.title}>{profile.catalogName}</div>
                    <div className={styles.subtitle}>{_(shortDescription)}</div>
                </div>
                {/* DESCRIPTION AND TECHNOLOGIES */}
                {profile.notes.length > 0 && (
                    <div className={styles.profileDescription}>
                        <div className={styles.profileInformation}>
                            <Title size='xl' underline className={styles.informationTitle}>
                                {_('Descripción general')}
                            </Title>
                            <Notes notes={notes} columns={2} />
                        </div>
                        {!!profile.technologies.length && (
                            <div className={styles.technologies}>
                                <TechnologyList
                                    type={type}
                                    brand={profile.brand}
                                    technologies={technologies}
                                    title='Tecnologías'
                                />
                            </div>
                        )}
                    </div>
                )}
                {/* DENSITY */}
                {this.hasDensity(profile, 'corn') && (
                    <div className={styles.section}>
                        <Title size='xl' underline>
                            {_('Densidad')}
                        </Title>
                        {this.hasDensityGraphic(profile) && (
                            <div style={{ maxWidth: '1000px' }}>
                                <CornDensityChart products={[profile]} />
                            </div>
                        )}
                        {this.hasDensityTable(profile) && !!profile.densities && !!profile.densities.length && (
                            <DensityTable
                                profile={profile}
                                type={type}
                                brand={brand}
                                environmentRange={environmentRange}
                            />
                        )}
                    </div>
                )}
                {this.hasDensity(profile, 'soy') &&
                    this.hasDensityTable(profile) &&
                    !!profile.densities &&
                    !!profile.densities.length && (
                        <div className={styles.section}>
                            <Title size='xl' underline>
                                {_('Densidad')}
                            </Title>
                            <DensityTable profile={profile} type={type} brand={brand} />
                        </div>
                    )}

                {/* METRICS */}
                {this.shouldRenderMetrics(profile) && (
                    <div className={styles.section}>
                        <Title size='xl' underline>
                            {_('Perfil defensivo')}
                        </Title>
                        <Metrics
                            highlight
                            dots
                            title={_('Perfil defensivo')}
                            dataKey='metrics'
                            brand={profile.brand}
                            products={[profile]}
                            country={this.props.productDatasheet.country}
                        />
                    </div>
                )}
                {/* FEATURES */}
                {this.shouldRenderFeatures(profile) && (
                    <div className={styles.section}>
                        <Title size='xl' underline>
                            {_('Características')}
                        </Title>
                        <Metrics
                            highlight
                            title={_('Características')}
                            dataKey='features'
                            products={[profile]}
                            brand={profile.brand}
                            country={this.props.productDatasheet.country}
                        />
                    </div>
                )}
                {/* REFUGE */}
                {this.shouldRenderRefuge(profile) && (
                    <div className={styles.section}>
                        <Refuge country={this.props.productDatasheet.country} />
                    </div>
                )}
            </div>
        );
    }

    render() {
        const { type, config, brand } = this.props.productDatasheet;
        return (
            <div>
                {this.shouldRenderOpening() && <Cover type={type} brand={brand} />}
                <CreatedBy user={this.props.user} />
                {this.props.productDatasheet.completePreview.map(this.renderProductDatasheet)}
                {/* BRAND VALUES */}
                {this.shouldRenderBrandValues() && (
                    <div className={styles.container}>
                        <BrandValues productDatasheet={this.props.productDatasheet.config} />
                    </div>
                )}
                {/* LEGAL TEXT */}
                {this.shouldRenderLegalText() && (
                    <div className={styles.container}>
                        <LegalText config={config} />
                    </div>
                )}
                {this.shouldRenderClosing() && <Closing type={type} brand={brand} />}
            </div>
        );
    }
}
